import React from 'react';
import styled from 'styled-components';
import { Border, Breakpoints, Color, Grid, Shadow, Type } from '../../../app/StyleGuide';
import { Card } from '../../../components/Layout';
import { CurriculumBrand, CurriculumPricing, CurriculumPricingEntity } from '../../curriculum';
import CurriculumCoverImage from './CurriculumCoverImage';
import { SmallTag } from './SmallTag';

const CardWrapper = styled.div<{ index: number }>`
  position: relative;
  max-width: 100%;
  height: 340px;
  z-index: ${props => (props.index ? 100 - props.index : 1)};
  transition: opacity 0.8s ease;

  @media (min-width: ${Breakpoints.screen_lg}) {
    &:hover {
      opacity: 1;
    }
  }

  @media (max-width: ${Breakpoints.screen_lg}) {
    height: auto;
    margin-bottom: 16px;
    opacity: 1;
  }
`;

const StyledCurriculumCard = styled(Card)`
  flex: 1;
  flex-direction: column;
  max-width: 100%;
  display: flex;
  height: 340px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  transition: all 0.7s ease;

  @media (min-width: ${Breakpoints.screen_lg}) {
    &:hover {
      height: auto;
      min-height: 340px;
      z-index: 1;

      .button-container {
        transform: translateY(0);
        opacity: 1;
      }
    }
  }

  @media (max-width: ${Breakpoints.screen_lg}) {
    height: 100%;
    min-height: auto;
    position: relative;

    .button-container {
      max-height: 500px;
      opacity: 1;
      padding: 15px 0;
    }
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
  justify-content: space-between;

  @media (max-width: ${Breakpoints.screen_lg}) {
    overflow: visible;
  }
`;

const CurrentTagCardWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
`;

const CurriculumCoverImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  max-height: 500px;
  transition: max-height 0.3s ease;

  @media (min-width: ${Breakpoints.screen_lg}) {
    ${StyledCurriculumCard}:hover & {
      max-height: 0px;
    }
  }
`;

const CardInfo = styled.div<{ $backgroundImage?: string }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
  min-height: 0;
  padding: ${Grid._4};

  background-color: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 45%;
    background: ${props => props.$backgroundImage || 'transparent'};
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;
    filter: blur(28px) saturate(110%);
    transform: scale(1.1);
    z-index: 0;
    pointer-events: none;

    -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0));
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0));
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 30%;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.65), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.25s ease;
    z-index: 0;
    pointer-events: none;
  }

  @media (min-width: ${Breakpoints.screen_lg}) {
    ${StyledCurriculumCard}:hover &::before {
      height: 100%;
    }

    ${StyledCurriculumCard}:hover &::after {
      opacity: 1;
    }
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const CardTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-size: 14px;
  height: 40px;
  opacity: 0.7;
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 0;
  opacity: 0;
  transition: all 0.5s ease;
  overflow: hidden;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 0;
  transform: translateY(0);

  @media (min-width: ${Breakpoints.screen_lg}) {
    ${StyledCurriculumCard}:hover & {
      max-height: 500px;
      opacity: 1;
      padding: 15px 0;
    }
  }

  &.hide {
    max-height: 0;
    opacity: 0;
    padding: 0;
    transform: translateY(-100%);
  }
`;

const CardButton = styled.button<{ isSelected: boolean }>`
  background-color: ${props => (props.isSelected ? Color.Gray._10 : Color.white)};
  border: ${props => (props.isSelected ? '2px' : '1px')} solid
    ${props => (props.isSelected ? Color.Blue._50 : Color.Gray._10)};
  color: ${Color.Gray._90};
  font-weight: ${Type.Weight.bold};
  width: 100%;
  padding: ${Grid._4} ${Grid._5};
  border-radius: ${Border.radius};
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  transition: all 0.2s ease;
  text-align: left;

  @media (min-width: ${Breakpoints.screen_lg}) {
    &:hover {
      background-color: ${Color.Gray._10};
      box-shadow: ${Shadow.medium};
    }
  }

  i {
    font-size: ${Type.Scale._1};
    color: ${Color.Gray._50};
  }
`;

const ChevronContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: 10px;
  z-index: 100;
  right: 0;
  left: 0;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: transform 0.3s ease;
  color: ${Color.Gray._50};

  i {
    transition: transform 0.3s ease;
  }

  @media (max-width: ${Breakpoints.screen_lg}) {
    display: none;
  }

  @media (min-width: ${Breakpoints.screen_lg}) {
    ${StyledCurriculumCard}:hover & i {
      transform: rotate(180deg);
    }
  }
`;

type CurriculumCardButtonProps = {
  brand: CurriculumBrand;
  curriculum: CurriculumPricingEntity;
  selectedButton?: { brand: CurriculumBrand; ageGroup: CurriculumPricingEntity };
  onButtonClick: (brand: CurriculumBrand, curriculum: CurriculumPricingEntity) => void;
  isCurrentPlan: boolean;
};

const CurriculumCardButton = ({
  brand,
  curriculum,
  selectedButton,
  onButtonClick,
  isCurrentPlan,
}: CurriculumCardButtonProps) => (
  <CardButton
    onClick={() => onButtonClick(brand, curriculum)}
    isSelected={!!selectedButton && selectedButton?.ageGroup.id === curriculum.id}
    data-qa-hook={`curriculum-card-button-${brand.code.toLowerCase()}-${curriculum.name.toLowerCase()}`}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: Grid._4 }}>
      {curriculum.name}
      {isCurrentPlan && <SmallTag style={{ backgroundColor: Color.Gray._20 }}>Current Subscription</SmallTag>}
    </div>
    <i className="fa fa-chevron-right" aria-hidden="true"></i>
  </CardButton>
);

type CurriculumCardProps = {
  curriculumGroup: CurriculumPricing;
  brand: CurriculumBrand;
  curriculumPackageName?: string;
  index: number;
  selectedButton?: { brand: CurriculumBrand; ageGroup: CurriculumPricingEntity };
  onButtonClick: (brand: CurriculumBrand, curriculum: CurriculumPricingEntity) => void;
  isCurrentSubscription: boolean;
};

export const CurriculumCard: React.FC<CurriculumCardProps> = ({
  brand,
  curriculumGroup,
  curriculumPackageName,
  index,
  selectedButton,
  onButtonClick,
  isCurrentSubscription,
}) => {
  return (
    <CardWrapper className="curriculum-card-wrapper" index={index}>
      <StyledCurriculumCard>
        <CardContent>
          <CurriculumCoverImageWrapper>
            <CurriculumCoverImage brand={brand} className="card-image" />
            <CurrentTagCardWrapper>
              {isCurrentSubscription && <SmallTag>Current Subscription</SmallTag>}
            </CurrentTagCardWrapper>
          </CurriculumCoverImageWrapper>
          <CardInfo $backgroundImage={brand?.background}>
            <CardTitle>{brand.name}</CardTitle>
            <CardSubtitle>{brand.description}</CardSubtitle>
            <ButtonContainer className="button-container">
              {curriculumGroup.curriculum.map((c, index) => {
                const isCurrentPlan =
                  isCurrentSubscription && curriculumPackageName?.toLowerCase() === c.name.toLowerCase();
                return (
                  <CurriculumCardButton
                    key={index}
                    brand={brand}
                    curriculum={c}
                    selectedButton={selectedButton}
                    onButtonClick={onButtonClick}
                    isCurrentPlan={isCurrentPlan}
                  />
                );
              })}
            </ButtonContainer>
          </CardInfo>
        </CardContent>
        <ChevronContainer>
          <i className="fa fa-chevron-down" aria-hidden="true"></i>
        </ChevronContainer>
      </StyledCurriculumCard>
    </CardWrapper>
  );
};

export default CurriculumCard;
